import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Loader2, RefreshCw, Trash2 } from 'lucide-react'
import { Button } from '@orisini/ui/components/button'
import { Card } from '@orisini/ui/components/card'
import { Input } from '@orisini/ui/components/input'
import { Label } from '@orisini/ui/components/label'
import { useConfirm } from '@orisini/ui/components/confirm'
import { api } from '@orisini/ui/lib/api'

interface FeedSource {
  id: string
  name: string
  url: string
  language: string | null
  active: boolean
  lastFetchedAt: number | null
  lastStatus: 'ok' | 'not_modified' | 'error' | null
  lastError: string | null
  lastItemCount: number | null
  items: number
}

interface FetchResult {
  status: 'ok' | 'not_modified' | 'error'
  found: number
  added: number
  error?: string
}

function when(ms: number | null): string {
  if (!ms) return 'Never'
  return new Date(ms).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false })
}

function laporHasil(name: string, r: FetchResult) {
  if (r.status === 'error') toast.error(`${name}: ${r.error ?? 'fetch failed'}`)
  else if (r.status === 'not_modified') toast.success(`${name}: no changes since the last fetch`)
  else toast.success(`${name}: ${r.found} items in the feed, ${r.added} new`)
}

/**
 * Admin → Feeds: daftar sumber berita Feed Engine.
 *
 * Orisini mengambil semua sumber aktif setiap 15 menit dan menyimpan itemnya
 * 3 hari; topik tiap akun memilih dari kumpulan itu. User tidak mengatur
 * sumber — hanya topiknya.
 */
export function FeedsPanel() {
  const confirm = useConfirm()
  const [sources, setSources] = useState<FeedSource[] | null>(null)
  const [form, setForm] = useState({ name: '', url: '', language: '' })
  const [busy, setBusy] = useState<string | null>(null)

  const muat = useCallback(async () => {
    try {
      const r = await api.get<{ sources: FeedSource[] }>('/admin/feeds')
      setSources(r.sources)
    } catch (err) {
      setSources([])
      toast.error(err instanceof Error ? err.message : "Couldn't load feeds")
    }
  }, [])

  useEffect(() => void muat(), [muat])

  const tambah = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy('add')
    try {
      const r = await api.post<{ source: FeedSource; fetch: FetchResult }>('/admin/feeds', {
        name: form.name,
        url: form.url,
        language: form.language || null,
      })
      laporHasil(r.source.name, r.fetch)
      setForm({ name: '', url: '', language: '' })
      await muat()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't add the feed")
    } finally {
      setBusy(null)
    }
  }

  const aksi = async (s: FeedSource, kind: 'fetch' | 'toggle' | 'delete') => {
    if (kind === 'delete') {
      const ok = await confirm({
        title: `Remove ${s.name}?`,
        description: `Its ${s.items} stored items are removed too. News already picked for an account stays.`,
        confirmLabel: 'Remove',
        destructive: true,
      })
      if (!ok) return
    }
    setBusy(`${kind}:${s.id}`)
    try {
      if (kind === 'fetch') {
        const r = await api.post<{ fetch: FetchResult }>(`/admin/feeds/${s.id}/fetch`, {})
        laporHasil(s.name, r.fetch)
      } else if (kind === 'toggle') {
        await api.patch(`/admin/feeds/${s.id}`, { active: !s.active })
      } else {
        await api.delete(`/admin/feeds/${s.id}`)
        toast.success(`${s.name} removed`)
      }
      await muat()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Active feeds are fetched every 15 minutes and their items are kept for 3 days. Each project's topics pick from these.
      </p>

      <Card className="p-5">
        <form onSubmit={tambah} className="grid gap-3 sm:grid-cols-[1fr_2fr_6rem_auto] sm:items-end">
          <div className="space-y-1.5">
            <Label htmlFor="feed-name">Name</Label>
            <Input id="feed-name" value={form.name} maxLength={80} placeholder="The Verge" onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="feed-url">Feed address (RSS or Atom)</Label>
            <Input id="feed-url" value={form.url} placeholder="https://…/rss" onChange={(e) => setForm({ ...form, url: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="feed-lang">Language</Label>
            <Input id="feed-lang" value={form.language} maxLength={8} placeholder="en" onChange={(e) => setForm({ ...form, language: e.target.value })} />
          </div>
          <Button type="submit" disabled={busy === 'add' || !form.name.trim() || !form.url.trim()}>
            {busy === 'add' && <Loader2 className="animate-spin" />}
            Add feed
          </Button>
        </form>
      </Card>

      {sources === null ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading…
        </div>
      ) : sources.length === 0 ? (
        <p className="text-sm text-muted-foreground">No feeds yet. Add the first one above.</p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {sources.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {s.name}
                  {s.language && <span className="ml-2 text-xs uppercase text-muted-foreground">{s.language}</span>}
                  {!s.active && <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">Paused</span>}
                </p>
                <p className="truncate text-xs text-muted-foreground">{s.url}</p>
                <p className={`text-xs ${s.lastStatus === 'error' ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {s.lastStatus === 'error'
                    ? `Last fetch failed (${when(s.lastFetchedAt)}): ${s.lastError}`
                    : `${s.items} items stored · last fetched ${when(s.lastFetchedAt)}`}
                </p>
              </div>
              <div className="flex shrink-0 gap-1">
                <Button variant="ghost" size="sm" disabled={!!busy} onClick={() => aksi(s, 'fetch')} title="Fetch now" aria-label="Fetch now">
                  {busy === `fetch:${s.id}` ? <Loader2 className="animate-spin" /> : <RefreshCw />}
                </Button>
                <Button variant="outline" size="sm" disabled={!!busy} onClick={() => aksi(s, 'toggle')}>
                  {s.active ? 'Pause' : 'Resume'}
                </Button>
                <Button variant="ghost" size="sm" disabled={!!busy} onClick={() => aksi(s, 'delete')} title="Remove" aria-label="Remove">
                  <Trash2 />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
